import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Route as RouteIcon, FileText, MapPin, MoreHorizontal, X, LogOut, UserCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeSegmented } from "@/components/ThemeToggle";
import { usePortal } from "@/pages/portal/PortalContext";

const BOTTOM_ITEMS = [
  { to: "/portal/overview",  label: "Home",      icon: Home },
  { to: "/portal/tour",      label: "Tour",      icon: MapPin },
  { to: "/portal/routing",   label: "Routing",   icon: RouteIcon },
  { to: "/portal/documents", label: "Docs",      icon: FileText },
];

/** Mobile top bar — 56px. Just brand + tour context + account. */
export const PortalMobileTopBar = ({ onOpenMore }) => {
  const { me, activeTour } = usePortal();
  return (
    <header
      className="md:hidden sticky top-0 z-40"
      style={{ background: "var(--bg-nav)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      data-testid="portal-mobile-top"
    >
      <div className="h-14 px-4 flex items-center gap-3">
        <BrandLogo forceDark height={22} withOpsMarker={false} />
        <div className="flex-1 min-w-0">
          <div className="uppercase text-[9.5px] tracking-[0.14em] font-semibold leading-tight"
               style={{ color: "#B7E31D" }}>
            Client Portal
          </div>
          <div className="text-[11.5px] text-white/70 leading-tight truncate">
            {activeTour?.display_name || me?.display_name || ""}
          </div>
        </div>
        <button
          onClick={onOpenMore}
          className="w-9 h-9 grid place-items-center rounded-md text-white/70 hover:text-white"
          data-testid="portal-mobile-account"
          aria-label="Account menu"
        >
          <UserCircle size={20} />
        </button>
      </div>
    </header>
  );
};

/** Mobile bottom nav — 5 items, active = lime icon + top-lime underline. */
export const PortalBottomNav = ({ onOpenMore }) => {
  const location = useLocation();
  return (
    <nav
      className="md:hidden fixed left-0 right-0 bottom-0 z-40"
      style={{
        background: "var(--bg-elevated)",
        borderTop: "1px solid var(--border-default)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="portal-bottom-nav"
    >
      <div className="grid grid-cols-5 h-14">
        {BOTTOM_ITEMS.map((it) => {
          const Icon = it.icon;
          const active = location.pathname.startsWith(it.to);
          return (
            <NavLink
              to={it.to}
              key={it.to}
              className="relative flex flex-col items-center justify-center gap-0.5"
              data-testid={`portal-bnav-${it.label.toLowerCase()}`}
            >
              {active && (
                <span className="absolute top-0 left-4 right-4 h-[3px] rounded-b"
                      style={{ background: "#B7E31D" }} />
              )}
              <Icon size={20}
                    style={{ color: active ? "#B7E31D" : "var(--fg-muted)" }} />
              <span className="text-[10.5px] font-medium"
                    style={{ color: active ? "var(--fg-primary)" : "var(--fg-muted)" }}>
                {it.label}
              </span>
            </NavLink>
          );
        })}
        <button
          onClick={onOpenMore}
          className="relative flex flex-col items-center justify-center gap-0.5"
          data-testid="portal-bnav-more"
        >
          <MoreHorizontal size={20} style={{ color: "var(--fg-muted)" }} />
          <span className="text-[10.5px] font-medium" style={{ color: "var(--fg-muted)" }}>More</span>
        </button>
      </div>
    </nav>
  );
};

/** More sheet — Quotes, Appearance, Account, Sign Out. Grouped with eyebrows. */
export const PortalMoreSheet = ({ open, onClose }) => {
  const nav = useNavigate();
  const { me, signOut } = usePortal();
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-50 flex items-end" data-testid="portal-more-sheet">
      <button
        aria-label="Close menu"
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onClose}
      />
      <div
        className="relative w-full rounded-t-2xl max-h-[85vh] overflow-y-auto"
        style={{
          background: "var(--bg-elevated)",
          borderTop: "1px solid var(--border-default)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
        }}
      >
        <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b"
             style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="text-[15px] font-semibold" style={{ color: "var(--fg-primary)" }}>
              {me?.display_name || "Client"}
            </div>
            <div className="text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
              Tour Manager · Client Portal
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md"
                  style={{ color: "var(--fg-muted)" }} data-testid="portal-more-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pt-4">
          <div className="uppercase text-[10px] tracking-[0.14em] font-semibold mb-1"
               style={{ color: "var(--fg-muted)" }}>Documents & Proposals</div>
          <button
            onClick={() => { nav("/portal/quotes"); onClose(); }}
            className="w-full text-left px-3 py-3 rounded-md text-[14px]"
            style={{ color: "var(--fg-primary)", background: "var(--bg-inset)" }}
            data-testid="portal-more-quotes"
          >
            Quotes
          </button>
        </div>

        <div className="px-4 pt-5">
          <div className="uppercase text-[10px] tracking-[0.14em] font-semibold mb-2"
               style={{ color: "var(--fg-muted)" }}>Appearance</div>
          <div className="flex items-center justify-between px-3 py-2.5 rounded-md"
               style={{ background: "var(--bg-inset)" }}>
            <span className="text-[13.5px]" style={{ color: "var(--fg-primary)" }}>Theme</span>
            <ThemeSegmented />
          </div>
        </div>

        <div className="px-4 pt-5">
          <div className="uppercase text-[10px] tracking-[0.14em] font-semibold mb-1"
               style={{ color: "var(--fg-muted)" }}>Account</div>
          <button
            onClick={() => { signOut(); }}
            className="w-full flex items-center gap-2 px-3 py-3 rounded-md text-[14px] font-medium"
            style={{ color: "#B83C3C", background: "rgba(184,60,60,0.06)" }}
            data-testid="portal-more-signout"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};
